import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  getAllSleepLogs,
  upsertSleepLog,
  deleteSleepLogByDate,
  getStreakInfo,
  getEntryCount,
  getSetting,
  setSetting,
} from '../db/sleepDB';
import {
  calculateSleepScore,
  calculateSleepDebt,
  calculateDuration,
  toDateString,
} from '../utils/sleepCalculations';
import * as api from '../services/api';
import { isIOS } from '../utils/platform';

// Auto-import from Apple Health at most once per 6 hours.
const HEALTH_AUTO_SYNC_INTERVAL_MS = 6 * 60 * 60 * 1000;

const SleepContext = createContext();

export function SleepProvider({ children }) {
  const [sleepLogs, setSleepLogs] = useState([]);
  const [streak, setStreak] = useState({ current: 0, longest: 0 });
  const [entryCount, setEntryCount] = useState(0);
  const [sleepGoal, setSleepGoalState] = useState(480);
  const [loading, setLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState('idle'); // idle | syncing | synced | error

  // Apple Health (iOS only) — connected flag + last import time live in Dexie
  // settings so they survive restarts and sign-out/sign-in on the same device.
  const [healthConnected, setHealthConnected] = useState(false);
  const [lastHealthImportAt, setLastHealthImportAt] = useState(null);
  const [healthSyncing, setHealthSyncing] = useState(false);

  const refreshData = useCallback(async () => {
    try {
      const [logs, streakInfo, count, goal] = await Promise.all([
        getAllSleepLogs(),
        getStreakInfo(),
        getEntryCount(),
        getSetting('sleepGoal'),
      ]);
      setSleepLogs(logs);
      setStreak(streakInfo);
      setEntryCount(count);
      if (goal) setSleepGoalState(goal);
    } catch (err) {
      console.error('Failed to refresh data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync with backend on mount
  const syncWithBackend = useCallback(async () => {
    setSyncStatus('syncing');
    try {
      const response = await api.getSleepHistory();
      if (response?.items?.length > 0) {
        // Merge backend data into local DB
        for (const item of response.items) {
          await upsertSleepLog({
            ...item,
            syncStatus: 'synced',
          });
        }
        await refreshData();
      }
      setSyncStatus('synced');
    } catch (err) {
      // Backend unavailable, continue with local data
      console.warn('Backend sync failed, using local data:', err.message);
      setSyncStatus('error');
    }
  }, [refreshData]);

  useEffect(() => {
    refreshData().then(() => {
      syncWithBackend();
    });
  }, [refreshData, syncWithBackend]);

  // `celebrateStreak` is opt-in (manual saves only) — HealthKit imports go
  // through the same function but must never fire the streak toast.
  const addSleepLog = useCallback(async (entry, { celebrateStreak = false } = {}) => {
    const durationMinutes = calculateDuration(entry.bedtime, entry.wakeTime);
    const recentLogs = sleepLogs.slice(0, 7);

    const scoreResult = calculateSleepScore(
      { ...entry, durationMinutes },
      recentLogs,
      entry.factors || null,
      sleepGoal
    );

    const fullEntry = {
      ...entry,
      date: entry.date || toDateString(new Date()),
      durationMinutes,
      sleepScore: scoreResult.total,
      scoreBreakdown: scoreResult.breakdown,
      grade: scoreResult.grade,
      weakestArea: scoreResult.weakestArea,
      tip: scoreResult.tip,
    };

    // Streak-before from the DB (not React state, which can lag mid-save).
    let streakBefore = 0;
    if (celebrateStreak) {
      try { streakBefore = (await getStreakInfo()).current; } catch { /* non-fatal */ }
    }

    // Save locally first (offline-first)
    await upsertSleepLog(fullEntry);
    await refreshData();

    // One-line celebration when a manual save grows the streak.
    if (celebrateStreak) {
      try {
        const { current } = await getStreakInfo();
        if (current > streakBefore) toast(`🔥 ${current}-day streak!`);
      } catch { /* celebration is best-effort */ }
    }

    // Then sync to backend (fire-and-forget)
    api.postSleep(fullEntry).catch(err => {
      console.warn('Failed to sync sleep to backend:', err.message);
    });

    // Also sync factors if present
    if (entry.factors) {
      api.postFactors({ date: fullEntry.date, ...entry.factors }).catch(err => {
        console.warn('Failed to sync factors to backend:', err.message);
      });
    }

    return { ...fullEntry, scoreResult };
  }, [sleepLogs, sleepGoal, refreshData]);

  // Latest addSleepLog without re-binding the health callbacks every render
  // (addSleepLog's identity changes whenever sleepLogs refreshes).
  const addSleepLogRef = useRef(addSleepLog);
  addSleepLogRef.current = addSleepLog;

  // Import recent nights from Apple Health through the normal addSleepLog
  // path. Existing logs are never overwritten. Returns { imported, skipped }.
  const syncHealthNow = useCallback(async () => {
    if (!isIOS()) return { imported: 0, skipped: 0 };
    setHealthSyncing(true);
    try {
      const { syncHealthKitNights } = await import('../services/healthImport');
      const result = await syncHealthKitNights({
        addSleepLog: (entry) => addSleepLogRef.current(entry),
      });
      const now = new Date().toISOString();
      await setSetting('lastHealthImportAt', now);
      setLastHealthImportAt(now);
      return result;
    } finally {
      setHealthSyncing(false);
    }
  }, []);

  // Requests HealthKit read permission and, on success, runs the first import.
  // Returns { ok, imported, skipped } or { ok: false, error } — the structured
  // error is surfaced by the Settings UI (never thrown).
  const connectHealth = useCallback(async () => {
    const { requestHealthPermissions } = await import('../services/health');
    const res = await requestHealthPermissions();
    if (!res.ok) return { ok: false, error: res.error };
    await setSetting('healthkitConnected', true);
    setHealthConnected(true);
    try {
      const result = await syncHealthNow();
      return { ok: true, ...result };
    } catch {
      // Connected but the first import hiccuped — auto-sync will retry.
      return { ok: true, imported: 0, skipped: 0 };
    }
  }, [syncHealthNow]);

  // Stops Drift reading Apple Health. iOS keeps the underlying permission —
  // that's managed in the Health app (the UI links there via openHealthSettings).
  const disconnectHealth = useCallback(async () => {
    await setSetting('healthkitConnected', false);
    setHealthConnected(false);
  }, []);

  // On app open (iOS only): hydrate the Health flags and, when connected,
  // auto-import — silently, throttled to once per 6 hours.
  const healthAutoSyncStarted = useRef(false);
  useEffect(() => {
    if (!isIOS() || healthAutoSyncStarted.current) return;
    healthAutoSyncStarted.current = true;
    (async () => {
      try {
        const [connected, lastImport] = await Promise.all([
          getSetting('healthkitConnected'),
          getSetting('lastHealthImportAt'),
        ]);
        setHealthConnected(Boolean(connected));
        if (lastImport) setLastHealthImportAt(lastImport);
        if (!connected) return;
        const last = lastImport ? new Date(lastImport).getTime() : 0;
        if (Date.now() - last < HEALTH_AUTO_SYNC_INTERVAL_MS) return;
        await syncHealthNow();
      } catch (err) {
        // Background operation — never surface a toast on app open.
        console.warn('Apple Health auto-sync failed:', err);
      }
    })();
  }, [syncHealthNow]);

  const removeSleepLog = useCallback(async (date) => {
    await deleteSleepLogByDate(date);
    await refreshData();
    // Local delete already shipped via Dexie; backend is best-effort.
    api.deleteSleep(date).catch(err => {
      console.warn('Failed to delete sleep from backend:', err.message);
    });
  }, [refreshData]);

  const updateSleepGoal = useCallback(async (minutes) => {
    await setSetting('sleepGoal', minutes);
    setSleepGoalState(minutes);

    // Sync to backend
    api.updateProfile({ sleepGoalHours: minutes / 60 }).catch(err => {
      console.warn('Failed to sync goal to backend:', err.message);
    });
  }, []);

  // Computed values
  const todayLog = sleepLogs.find(l => l.date === toDateString(new Date()));
  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterdayLog = sleepLogs.find(l => l.date === toDateString(yesterdayDate));
  const lastLog = todayLog || yesterdayLog || sleepLogs[0];

  const last7Logs = sleepLogs.slice(0, 7);
  const last30Logs = sleepLogs.slice(0, 30);
  const sleepDebt = calculateSleepDebt(sleepLogs, sleepGoal);

  const weeklyAvg = last7Logs.length > 0
    ? Math.round(last7Logs.reduce((s, l) => s + (l.sleepScore || 0), 0) / last7Logs.length)
    : null;

  return (
    <SleepContext.Provider value={{
      sleepLogs,
      last7Logs,
      last30Logs,
      lastLog,
      todayLog,
      streak,
      entryCount,
      sleepGoal,
      sleepDebt,
      weeklyAvg,
      loading,
      syncStatus,
      addSleepLog,
      removeSleepLog,
      updateSleepGoal,
      refreshData,
      healthConnected,
      lastHealthImportAt,
      healthSyncing,
      connectHealth,
      disconnectHealth,
      syncHealthNow,
    }}>
      {children}
    </SleepContext.Provider>
  );
}

export const useSleep = () => useContext(SleepContext);
